/**
 * /admin/intl-ops — unified ops console for the 5 intl Lead Marketplace
 * personas + the Kennel Verified Inquiry gate. Three sections:
 *
 *   1. Lead Marketplace overview — pros, credits, subs, revenue
 *   2. Kennel Verified Inquiry — paid inquiries + response rate
 *   3. Performance bonds — list + one-click "mark as refunded"
 *
 * Built to be the single dashboard the founder opens to answer
 * "how's the new lead-marketplace doing?" without poking around in
 * /admin/data direct collection edits.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeftIcon as ArrowLeft, GlobeHemisphereWestIcon as GlobeHemisphereWest, ShieldCheckIcon as ShieldCheck, ArrowClockwiseIcon as ArrowClockwise, EnvelopeIcon as Envelope } from "@phosphor-icons/react";

const personaLabel = {
  intl_transporter: "Intl couriers",
  intl_walker: "Intl walkers",
  intl_sitter: "Intl sitters",
  intl_groomer: "Intl groomers",
  intl_trainer: "Intl trainers",
};

const Card = ({ title, value, sub, testId, accent = "#0E0E0E" }) => (
  <div className="rounded-xl border border-[#EAEAEA] bg-white p-4" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-1">{title}</div>
    <div className="text-2xl font-serif tracking-tight" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[11px] text-[#9A9A9A] mt-0.5">{sub}</div>}
  </div>
);

const SectionHeader = ({ icon: Icon, label, color, action }) => (
  <div className="flex items-end justify-between mb-4">
    <div className="flex items-center gap-2.5">
      <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
        <Icon size={18} weight="duotone" />
      </span>
      <h2 className="font-spectral text-2xl tracking-tight">{label}</h2>
    </div>
    {action}
  </div>
);

function LeadMarketplaceSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/intl-ops/lead-marketplace?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load lead marketplace stats"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6 mb-5" data-testid="intl-ops-lead-marketplace">
      <SectionHeader
        icon={GlobeHemisphereWest}
        label="Lead Marketplace"
        color="#C9A227"
        action={
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
                  days === d
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-ink"
                }`}
                data-testid={`lm-range-${d}d`}
              >
                {d}d
              </button>
            ))}
          </div>
        }
      />
      {loading || !data ? (
        <div className="text-sm text-[#6B6B6B] py-6">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Card title="Platform revenue" value={`$${data.platform_revenue_usd.toLocaleString()}`} sub={`last ${data.days}d`} testId="lm-revenue" accent="#0F8043" />
            <Card title="Leads delivered" value={data.leads_delivered.total.toLocaleString()} sub={`${data.leads_delivered.intl_couriers} couriers · ${data.leads_delivered.intl_other_pros} other`} testId="lm-leads" />
            <Card title="Total pros listed" value={(data.pros_total.intl_couriers + data.pros_total.intl_other_pros).toLocaleString()} sub="all-time" testId="lm-pros" />
            <Card title="Pro Sub MRR" value={`$${data.active_subs.reduce((s, x) => s + x.mrr_usd, 0).toLocaleString()}`} sub={`${data.active_subs.reduce((s, x) => s + x.count, 0)} subscribers`} testId="lm-mrr" accent="#C9A227" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-2">Active Pro Subs</h3>
              {data.active_subs.length === 0 ? (
                <div className="text-xs text-[#9A9A9A]">No active subs yet.</div>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {data.active_subs.map((s) => (
                    <li key={s.persona} className="flex justify-between border-b border-[#F3F3F3] pb-1">
                      <span>{personaLabel[s.persona] || s.persona}</span>
                      <span className="font-semibold">{s.count} · ${s.mrr_usd}/mo</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-2">Outstanding credits</h3>
              {data.credit_balances.length === 0 ? (
                <div className="text-xs text-[#9A9A9A]">No outstanding credit packs.</div>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {data.credit_balances.map((c) => (
                    <li key={c.persona} className="flex justify-between border-b border-[#F3F3F3] pb-1">
                      <span>{personaLabel[c.persona] || c.persona}</span>
                      <span className="font-semibold">{c.total_credits_remaining} credits · {c.pros_with_credits} pros</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-2">Lead charges ({data.days}d)</h3>
              {data.lead_charges.length === 0 ? (
                <div className="text-xs text-[#9A9A9A]">No PPL or credit-pack purchases yet.</div>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {data.lead_charges.map((c) => (
                    <li key={c.kind} className="flex justify-between border-b border-[#F3F3F3] pb-1">
                      <span>{c.kind}</span>
                      <span className="font-semibold">{c.count} · ${c.revenue_usd}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function KennelInquirySection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/intl-ops/kennel-inquiries?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load kennel inquiries"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6 mb-5" data-testid="intl-ops-kennel-inquiries">
      <SectionHeader
        icon={Envelope}
        label="Kennel Verified Inquiries"
        color="#C1272D"
        action={
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
                  days === d
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-ink"
                }`}
                data-testid={`ki-range-${d}d`}
              >
                {d}d
              </button>
            ))}
          </div>
        }
      />
      {loading || !data ? (
        <div className="text-sm text-[#6B6B6B] py-6">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Card title="Revenue" value={`$${data.revenue_usd.toLocaleString()}`} sub={`last ${data.days}d`} testId="ki-revenue" accent="#0F8043" />
            <Card title="Paid inquiries" value={data.paid_inquiries.toLocaleString()} sub={`of ${data.total_inquiries} total`} testId="ki-paid" />
            <Card title="Response rate" value={`${data.response_rate_pct}%`} sub={`${data.responded_within_7d} within 7d`} testId="ki-response" accent="#C9A227" />
            <Card title="Bypasses" value={(data.elite_bypass + data.grandfather_bypass).toLocaleString()} sub={`${data.elite_bypass} Elite · ${data.grandfather_bypass} grandfather`} testId="ki-bypass" />
          </div>

          <div>
            <h3 className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-2">Top kennels by paid inquiries</h3>
            {data.top_kennels.length === 0 ? (
              <div className="text-xs text-[#9A9A9A]">No paid inquiries yet — the gate just launched.</div>
            ) : (
              <ol className="space-y-1.5 text-sm">
                {data.top_kennels.map((k, i) => (
                  <li key={k.kennel_id} className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-[#9A9A9A] w-5 text-right">{i + 1}</span>
                    <span className="flex-1 truncate">{k.kennel_name}</span>
                    <span className="rounded-full bg-ink text-white text-[10px] font-semibold px-2 py-0.5">{k.paid_inquiries}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function VerificationsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/intl-ops/verifications`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load verifications"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const tierLabel = { 1: "Tier 1 (verified)", 2: "Tier 2 (3+ deliveries)", 3: "Tier 3 (10+ deliveries)" };
  const tierColor = { 1: "#9A9A9A", 2: "#C9A227", 3: "#0F8043" };

  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6 mb-5" data-testid="intl-ops-verifications">
      <SectionHeader
        icon={ShieldCheck}
        label="Verifications & trust ladder"
        color="#0F8043"
        action={
          <Button size="sm" variant="outline" onClick={load} data-testid="verifications-reload-btn">
            <ArrowClockwise size={13} weight="bold" /> <span className="ml-1">Reload</span>
          </Button>
        }
      />

      <p className="text-xs text-[#6B6B6B] mb-4 max-w-2xl">
        Every free-tier intl pro pays a one-time <b>$9 verification fee</b>
        (non-refundable revenue &mdash; <i>not</i> a held bond). After
        verification, pros enter the trust ladder: 1 lead/mo at Tier 1,
        2/mo after 3 buyer-confirmed deliveries, 4/mo after 10. No money
        ever held; no refund obligation; no liability exposure.
      </p>

      {loading || !data ? (
        <div className="text-sm text-[#6B6B6B] py-6">Loading verifications…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Card title="Verification revenue" value={`$${data.total_verification_revenue_usd.toLocaleString()}`} sub="all-time, non-refundable" testId="ver-revenue" accent="#0F8043" />
            <Card title="Verified pros" value={data.count.toLocaleString()} sub="across all 5 intl personas" testId="ver-count" />
            <Card title="Trust Tier 1" value={data.tier_distribution[1]?.toLocaleString() || 0} sub="1 lead / month" testId="ver-tier1" accent="#9A9A9A" />
            <Card title="Trust Tier 3" value={data.tier_distribution[3]?.toLocaleString() || 0} sub="4 leads/mo + featured-eligible" testId="ver-tier3" accent="#0F8043" />
          </div>

          {data.verifications.length === 0 ? (
            <div className="text-sm text-[#6B6B6B] bg-[#FAFAFA] rounded-lg p-4 border border-dashed border-[#E5E5E5]" data-testid="ver-empty-state">
              No verified pros yet &mdash; the $9 verification flow goes live
              when intl signups open. This view will populate as soon as the
              first pro completes Stripe checkout.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">
                    <th className="text-left py-2">Pro</th>
                    <th className="text-left py-2">Persona</th>
                    <th className="text-right py-2">Deliveries</th>
                    <th className="text-right py-2">Quota</th>
                    <th className="text-left py-2">Tier</th>
                    <th className="text-right py-2">Fee paid</th>
                  </tr>
                </thead>
                <tbody>
                  {data.verifications.map((v) => (
                    <tr key={v.id} className="border-t border-[#F3F3F3]" data-testid={`ver-row-${v.id}`}>
                      <td className="py-2">{v.pro_email || v.pro_id?.slice(0, 8) || "—"}</td>
                      <td className="py-2 text-[#6B6B6B]">{personaLabel[v.persona_kind] || v.persona_kind}</td>
                      <td className="py-2 text-right">{v.deliveries}</td>
                      <td className="py-2 text-right font-semibold">{v.monthly_lead_quota}/mo</td>
                      <td className="py-2">
                        <span className="rounded-full text-[10px] px-2 py-0.5 font-semibold" style={{ background: `${tierColor[v.tier]}15`, color: tierColor[v.tier] }}>
                          {tierLabel[v.tier]}
                        </span>
                      </td>
                      <td className="py-2 text-right text-[#6B6B6B]">${v.verification_fee_paid_usd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default function AdminIntlOps() {
  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-intl-ops-page">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-[#6B6B6B] hover:text-ink text-sm mb-4" data-testid="back-to-admin">
          <ArrowLeft size={14} weight="bold" /> Back to admin
        </Link>
        <header className="mb-8 pb-6 border-b border-[#E5E5E5]">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#6B6B6B] font-bold mb-2">Intl Lead Marketplace · Kennel Verified Inquiry · Performance bonds</p>
          <h1 className="font-spectral text-3xl sm:text-4xl text-ink">Intl Lead Marketplace Ops</h1>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
            Single dashboard for everything that shipped after the 5 intl personas
            and Kennel Verified Inquiry launched. Activity will start filling in
            as pros apply and buyers send paid inquiries.
          </p>
        </header>

        <LeadMarketplaceSection />
        <KennelInquirySection />
        <VerificationsSection />
      </div>
    </AppShell>
  );
}
